import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Zap, CheckCircle2, Loader2, Calendar, CreditCard, History } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const categoryMap = {
  bonus: 7,
  penalty: 8,
  insurance: 2,
  toll: 7,
  correction: 8,
};

export default function WalletDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [submittingAdjustment, setSubmittingAdjustment] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: '',
    type: 'credit',
    description: '',
    category: 'bonus',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/wallets/${id}`);
      const payload = data.data || data;
      setWalletData(payload.wallet || payload);
      setTransactions(payload.transactions || []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar dados da wallet');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => {
    const inflow = transactions.filter((t) => parseFloat(t.amount) > 0).reduce((acc, t) => acc + parseFloat(t.amount), 0);
    const outflow = transactions.filter((t) => parseFloat(t.amount) < 0).reduce((acc, t) => acc + Math.abs(parseFloat(t.amount)), 0);
    return { inflow, outflow, ratio: inflow > 0 ? (outflow / inflow) * 100 : 0 };
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) =>
      (t.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.category_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [transactions, searchTerm]);

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    if (!adjustmentForm.amount || Number(adjustmentForm.amount) <= 0 || !adjustmentForm.description) {
      toast.error('Parâmetros de ajuste incompletos.');
      return;
    }

    setSubmittingAdjustment(true);
    try {
      await api.post(`/wallets/${id}/adjustments`, {
        ...adjustmentForm,
        category_id: categoryMap[adjustmentForm.category],
      });
      toast.success('Ledger Atualizado', { description: 'Ajuste consolidado.' });
      setShowAdjustmentModal(false);
      setAdjustmentForm({ amount: '', type: 'credit', description: '', category: 'bonus' });
      loadData();
    } catch (err) {
      toast.error('Falha na Injeção', { description: 'O motor financeiro rejeitou o ajuste manual.' });
    } finally {
      setSubmittingAdjustment(false);
    }
  };

  const handleIndividualPayout = async () => {
    if (!walletData) return;
    if (!window.confirm(`Confirmar liquidação de ${parseFloat(walletData.current_balance).toFixed(2)}€?`)) return;
    try {
      setPayoutLoading(true);
      await api.post('/payouts', {
        driver_id: id,
        amount: parseFloat(walletData.current_balance),
        payout_method: 'cash',
        description: `Liquidação rápida via wallet detail - motorista #${id}`,
      });
      toast.success('Liquidação Concluída', { description: 'Saldo zerado e auditado.' });
      loadData();
    } catch (err) {
      toast.error('Erro de Payout', { description: 'Falha no protocolo de liquidação.' });
    } finally {
      setPayoutLoading(false);
    }
  };

  if (loading || !walletData) {
    return (
      <div className="flex h-80 flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">A carregar snapshot financeiro...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2 md:p-4">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/finance/wallets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {walletData.first_name} {walletData.last_name}
            </h1>
            <p className="text-xs text-muted-foreground">Ledger ID: #{walletData.wallet_id} · {walletData.active_plan || 'Sem plano'}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowAdjustmentModal(true)}>
            <Zap className="mr-2 h-4 w-4" /> Injetar Ajuste
          </Button>
          <Button
            onClick={handleIndividualPayout}
            disabled={payoutLoading || parseFloat(walletData.current_balance) <= 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {payoutLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Liquidar Saldo
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-8">
          <CardContent className="py-8">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Disponibilidade em Carteira</p>
            <h2 className={`mt-2 text-5xl font-bold ${parseFloat(walletData.current_balance) < 0 ? 'text-destructive' : 'text-foreground'}`}>
              {parseFloat(walletData.current_balance).toLocaleString('pt-PT', { minimumFractionDigits: 2 })}€
            </h2>
            <div className="mt-6 flex gap-6 border-t pt-6">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Entradas</p>
                <p className="text-xl font-bold text-emerald-600">+{stats.inflow.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Saídas</p>
                <p className="text-xl font-bold text-destructive">-{stats.outflow.toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-4 bg-primary text-primary-foreground">
          <CardContent className="flex h-full flex-col justify-between py-8">
            <div>
              <CreditCard className="mb-4 h-8 w-8 opacity-60" />
              <h3 className="text-xl font-bold">Auditoria em Tempo Real</h3>
              <p className="mt-2 text-sm opacity-80">Todas as transações são registadas no Ledger com ID de referência único.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary" />
            <CardTitle>Histórico de Movimentos</CardTitle>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Pesquisar transação..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-3 pl-4 font-medium text-muted-foreground">Data e Hora</th>
                  <th className="py-3 font-medium text-muted-foreground">Descrição</th>
                  <th className="py-3 text-center font-medium text-muted-foreground">Categoria</th>
                  <th className="py-3 pr-4 text-right font-medium text-muted-foreground">Montante</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((t) => (
                  <tr key={t.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-4 pl-4">
                      <p className="font-semibold">{new Date(t.created_at).toLocaleDateString('pt-PT')}</p>
                      <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</p>
                    </td>
                    <td className="py-4">
                      <p className="font-semibold">{t.description}</p>
                      <p className="text-xs text-muted-foreground">Ref: {t.reference_type} #{t.reference_id || 'MNL'}</p>
                    </td>
                    <td className="py-4 text-center">
                      <span className="rounded-md bg-muted px-2 py-1 text-xs font-semibold">{t.category_name || 'Geral'}</span>
                    </td>
                    <td className={`py-4 pr-4 text-right font-bold ${parseFloat(t.amount) < 0 ? 'text-destructive' : 'text-foreground'}`}>
                      {parseFloat(t.amount) > 0 ? '+' : ''}{parseFloat(t.amount).toFixed(2)}€
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showAdjustmentModal} onOpenChange={setShowAdjustmentModal}>
        <DialogContent onClose={() => setShowAdjustmentModal(false)}>
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Zap className="h-5 w-5" />
              </div>
              <DialogTitle>Injeção Manual</DialogTitle>
            </div>
          </DialogHeader>

          <form onSubmit={handleAdjustmentSubmit} className="space-y-4 pt-2">
            <div className="flex gap-2 rounded-lg bg-muted p-1">
              <Button
                type="button"
                size="sm"
                variant={adjustmentForm.type === 'credit' ? 'default' : 'ghost'}
                className="flex-1"
                onClick={() => setAdjustmentForm({ ...adjustmentForm, type: 'credit' })}
              >
                Crédito (+)
              </Button>
              <Button
                type="button"
                size="sm"
                variant={adjustmentForm.type === 'debit' ? 'destructive' : 'ghost'}
                className="flex-1"
                onClick={() => setAdjustmentForm({ ...adjustmentForm, type: 'debit' })}
              >
                Débito (-)
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Montante (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  required
                  value={adjustmentForm.amount}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Categoria</label>
                <Select
                  value={adjustmentForm.category}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, category: e.target.value })}
                >
                  <option value="bonus">Bónus</option>
                  <option value="penalty">Multa / Dano</option>
                  <option value="insurance">Seguro</option>
                  <option value="toll">Portagem</option>
                  <option value="correction">Correção</option>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Justificativa Auditada</label>
              <textarea
                required
                rows={3}
                className="w-full rounded-md border border-input bg-transparent p-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Motivo detalhado da intervenção..."
                value={adjustmentForm.description}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, description: e.target.value })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdjustmentModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submittingAdjustment}>
                {submittingAdjustment ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Consolidar Ajuste'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
